// @workflow assistant.clarification
import {
  pendingClarification,
  invalidClarifications,
  clarificationAnswerFor,
  withClarificationAnswer,
  withExpiredRequest,
  expiredQuestionId,
} from '@Assistant/domain/clarificationState';
import {
  clarificationStepsView,
  clarificationSubmission,
  withClarificationPick,
  withClarificationText,
  withClarificationStep,
} from '@Assistant/domain/clarificationDrafts';
import { answerBody } from '@Assistant/domain/clarification';
import { openTextQuestion } from '@Assistant/domain/textQuestion';
import { error as debugError } from '@/utils/debugLogger';

/**
 * The clarification part of the ASSISTANT store (contract "Clarifications: ask, don't guess"),
 * spread into assistant.modules.js. The rules live in domain/clarification*.js.
 */
export const CLARIFICATION_STATE = () => ({
  /** The text question (domain/textQuestion.js) whose panel the user closed: its reply shows it again. */
  closedTextQuestionId: null,
});

export const CLARIFICATION_MUTATIONS = Object.freeze({
  SET_CLOSED_TEXT_QUESTION(state, requestId) { state.closedTextQuestionId = requestId; },
});

export const CLARIFICATION_GETTERS = Object.freeze({
  /** The question the docked panel shows (a pending clarification of an awaiting reply), or null. */
  PENDING_CLARIFICATION: (s) => pendingClarification(s.conversation),
  /**
   * A question the last reply asked as text with listed choices (domain/textQuestion.js), while the
   * panel shows it: not while a reply is on its way, and not once the user closed it. Its reply
   * leaves the question out of the text meanwhile (one source for the panel and the thread).
   */
  OPEN_TEXT_QUESTION: (s, getters) => (getters.IS_STREAMING ? null : openTextQuestion(s.conversation, s.closedTextQuestionId)),
  /** Clarifications that broke the contract: the UI answers them dismissed and never shows them. */
  INVALID_CLARIFICATIONS: (s) => invalidClarifications(s.conversation),
  /** The expired question the next typed message will quote, or null. */
  EXPIRED_QUESTION_ID: (s) => expiredQuestionId(s.conversation),
  /**
   * The stepper of a pending clarification (domain/clarificationDrafts.js): the question on screen,
   * its draft, which questions have an answer, whether Submit is on. Kept in the store, so closing
   * the composer (History, Settings) loses no picks. Null when it is not pending.
   */
  CLARIFICATION_STEPS: (s) => (requestId) => clarificationStepsView(s.conversation, requestId),
});

/**
 * @param {{ detachRun: () => void }} deps detachRun forgets the paused run without stopping it
 *   (its stream is already closed, and stopping would mark the reply stopped)
 */
export function clarificationActions({ detachRun }) {
  return {
    /**
     * Answers a clarification: the thread shows the answer at once, then it goes through the
     * same resolve path as approvals. A second answer for the same question, or one that leaves a
     * question unanswered, is ignored. What is posted is the checked answer, never the raw payload:
     * `{ answer: { answers: [...] } }` or `{ answer: { dismissed: true } }`.
     * @param {{ requestId: string, answer: { answers: Array } | { optionIds: string[], text?: string } | { dismissed: true } }} payload
     * @returns {boolean} the answer was recorded and sent
     */
    ANSWER_CLARIFICATION({ commit, state, dispatch }, { requestId, answer }) {
      const clean = clarificationAnswerFor(state.conversation, requestId, answer);
      if (!clean) {
        // Not a whole answer (a question left out), or no such pending question: never sent.
        debugError('[AvaAssistant] Clarification answer refused', { requestId, answer });
        return false;
      }
      const next = withClarificationAnswer(state.conversation, requestId, clean);
      if (next === state.conversation) return false;
      commit('SET_CONVERSATION', next);
      dispatch('RESOLVE_CLIENT_REQUEST', { requestId, response: answerBody(clean) });
      return true;
    },

    /**
     * A pick on the question on screen; a one-question single-select pick is the answer at once.
     * @returns {Promise<boolean>|boolean} an answer was sent
     */
    PICK_CLARIFICATION_OPTION({ commit, state, dispatch }, { requestId, optionId }) {
      const { conversation, submit } = withClarificationPick(state.conversation, requestId, optionId);
      if (conversation !== state.conversation) commit('SET_CONVERSATION', conversation);
      return submit ? dispatch('ANSWER_CLARIFICATION', { requestId, answer: submit }) : false;
    },

    /** What the user typed answers the question on screen. */
    TYPE_CLARIFICATION_TEXT({ commit, state }, { requestId, text }) {
      const next = withClarificationText(state.conversation, requestId, text);
      if (next !== state.conversation) commit('SET_CONVERSATION', next);
    },

    /** Shows another question (Back, Next, ← →). */
    GO_TO_CLARIFICATION_STEP({ commit, state }, { requestId, index }) {
      const next = withClarificationStep(state.conversation, requestId, index);
      if (next !== state.conversation) commit('SET_CONVERSATION', next);
    },

    /**
     * Submit: every question's draft as one answer; refused (and logged) while one is unanswered.
     * @returns {Promise<boolean>|boolean} the answer was sent
     */
    SUBMIT_CLARIFICATION({ state, dispatch }, requestId) {
      const answer = clarificationSubmission(state.conversation, requestId);
      if (!answer) {
        debugError('[AvaAssistant] Clarification submitted with a question unanswered', { requestId });
        return false;
      }
      return dispatch('ANSWER_CLARIFICATION', { requestId, answer });
    },

    /** `expiresAt` passed while the reply waited: the request closes and the paused reply ends. */
    EXPIRE_CLIENT_REQUEST({ commit, state }, requestId) {
      const next = withExpiredRequest(state.conversation, requestId);
      if (next === state.conversation) return;
      detachRun();
      commit('SET_CONVERSATION', next);
    },
  };
}
